import { I9nSyncScope, OrderValidationMode, PaymentTerminalStatusRecoveryStep, UserRight, } from '@biteinc/enums';
export const biteSettingsSchema = {
    // Location Model Settings
    // BITE SETTINGS
    rejectVitrineOrdersWithAbsentDefaultMods: {
        displayName: 'Reject Vitrine Orders With Absent Default Modifiers',
        type: 'bool',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            isWarning: true,
            text: 'If enabled, then the order will be rejected if a default modifier is neither selected nor deselected. Not all Vitrine builds are compatible with this setting.',
        },
        exclusiveTo: 'vitrine',
    },
    enableMultipleQuantityItems: {
        displayName: 'Enable Multiple Quantity items',
        type: 'bool',
        required: true,
        tabId: 'bite-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'Enabling this sets quantity multiplier of ordered items in the order.',
        },
        exclusiveTo: 'gcn',
    },
    minOrderSubTotalIncreaseForWarning: {
        displayName: 'Min Order Subtotal Increase (Dollar Value) To Show Warning',
        type: 'price',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        max: 15000,
    },
    minOrderSubTotalDecreaseForWarning: {
        displayName: 'Min Order Subtotal Decrease (Dollar Value) To Show Warning',
        type: 'price',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        max: 300,
    },
    orderValidationMode: {
        displayName: 'Order Validation Mode',
        type: 'stringEnum',
        enum: OrderValidationMode,
        required: true,
        biteOnly: true,
        ui: 'dropdown',
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Controls how orders are validated. "Default": only at checkout. "On Add": synchronously on every update. "Background": validation runs in the background after order start/update without blocking the UI. "Background" requires a POS that supports concurrent validation.',
        },
        exclusiveTo: 'vitrine',
    },
    vitrineTracesSamplePercent: {
        displayName: 'Vitrine Effect Trace Sample Percent',
        type: 'int',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        min: 0,
        max: 100,
        tooltip: {
            text: 'Percent of Vitrine Effect traces sent to Sentry. 0 turns tracing off and skips the tracer entirely. Kiosks read this value at page load.',
            isWarning: true,
        },
        exclusiveTo: 'vitrine',
    },
    refundAbandonedStoredValueTransactions: {
        displayName: 'Graceful failure: Refund abandoned V2 orders with a stored value transaction quickly',
        type: 'bool',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Refunds all transactions on an abandoned V2 order if at least one transaction is a stored value transaction.',
        },
        exclusiveTo: 'gcn',
    },
    refundAbandonedV2Transactions: {
        displayName: 'Graceful failure: Refund abandoned V2 transactions quickly',
        type: 'bool',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        exclusiveTo: 'gcn',
    },
    refundAbandonedV3Transactions: {
        displayName: 'Graceful failure: Refund abandoned V3 transactions quickly',
        type: 'bool',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        exclusiveTo: 'vitrine',
    },
    refundFailedToSendOrders: {
        displayName: 'Graceful failure: Refund "failed to send" orders quickly',
        type: 'bool',
        required: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Not available for multi-vendor',
        },
    },
    logExpoPrinterJobsUntil: {
        displayName: 'Log Expo Printer Jobs Until',
        type: 'timestamp',
        required: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Log Expo printer job details (order IDs) until this date. After the date entered elapses, no more logs will be generated.',
        },
        showDatePickerOnly: true,
        pastDateLimit: 'disallowed',
        futureDateLimit: { value: 7, unit: 'days' },
        isNullable: true,
        writeRight: UserRight.BiteOps,
    },
    logKioskPaymentTerminalStatusChecksUntil: {
        displayName: 'Log Kiosk Payment Terminal Status Checks Until',
        type: 'timestamp',
        required: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Only for payment integrations that fetch terminal status from a cloud API. After the date entered elapses, no more logs will be generated.',
        },
        showDatePickerOnly: true,
        pastDateLimit: 'disallowed',
        futureDateLimit: { value: 7, unit: 'days' },
        isNullable: true,
        writeRight: UserRight.BiteOps,
    },
    paymentTerminalStatusRecoveryStep: {
        displayName: 'Payment Terminal Status Recovery Step (Worldpay)',
        type: 'stringEnum',
        enum: PaymentTerminalStatusRecoveryStep,
        required: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'Defines the recovery step to perform when a payment terminal is detected as offline. "None" means no action will be taken. "Reboot" will attempt to reboot the terminal. "Idle Text" will attempt to update the idle text on the terminal, which has been found to recover some offline terminals.',
            isWarning: true,
        },
    },
    posAutomaticSyncScope: {
        type: 'stringEnum',
        enum: I9nSyncScope,
        displayName: 'POS Automatic Sync Scope',
        required: true,
        tabId: 'bite-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'Determines what menu data is automatically synced from the POS. Manual syncs will fetch the full menu.',
        },
    },
    alertWhenQuicksandChangesAreDetected: {
        displayName: 'Alert When Quicksand Changes Are Detected',
        type: 'bool',
        required: true,
        biteOnly: true,
        defaultValue: false,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: 'If enabled, then an email alert with a summary of the changes will be sent to support@getbite.com whenever a POS integration sync changes the menu at this channel.',
        },
    },
    syncOpenHoursFromGoogle: {
        displayName: 'Sync Open Hours From Google',
        type: 'bool',
        required: true,
        biteOnly: true,
        defaultValue: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            text: "If enabled, then the channels's open hours will be synced from Google. This does not affect channels which sync open hours from the POS.",
        },
    },
    biteCustomOrderNumber: {
        displayName: 'Bite Custom Order Number',
        type: 'object',
        required: true,
        isNullable: true,
        biteOnly: true,
        tabId: 'bite-settings-v2',
        tooltip: {
            isWarning: true,
            text: 'When set, new orders get a client order number that cycles through the range (e.g. 101-199) instead of the standard 4-digit number, and the kiosk prefix is dropped. The bounds must differ by at least 50. Displayed everywhere, including receipts, reports, and the POS.',
        },
        fields: {
            lowerBound: {
                displayName: 'Lower Bound',
                type: 'int',
                required: true,
                min: 0,
                max: 99999,
            },
            upperBound: {
                displayName: 'Upper Bound',
                type: 'int',
                required: true,
                min: 0,
                max: 99999,
            },
        },
    },
};
//# sourceMappingURL=settings_bite_schema.js.map